import React from 'react'
import axios, { AxiosRequestConfig, Method } from 'axios'
import Cookie from 'js-cookie'
import { get } from 'lodash'
import { isServer } from '~/framework/utils/env'
import { Link, Message } from '@arco-design/web-react'
import qs from 'querystring'
import _ from 'lodash'
import { apiError } from '@byted-vcloud/exp-lite-slardar'
export interface BaseOption {
  /** 直接返回OpenApi的Result字段 默认为true **/
  returnResult: boolean
  /** OpenApi报错时弹出消息 默认为true **/
  messageError: boolean
  /** 业务错误时throw 默认为false **/
  throwBizError: boolean
  /** 未登录时自动重定向 默认为true **/
  autoRedirect: boolean
  /** 请求域名 默认请求当前域 需要携带协议 **/
  requestOrigin: string
  /**http method，默认为get */
  method: Method
  /**json请求 默认false */
  isJson: boolean
  /**区域配置 默认cn-north-1 */
  region: string
  /**额外的query配置 常见于post请求携带query的情况 */
  query: any
  /** axios 请求配置 */
  axiosOptions: AxiosRequestConfig
  /** 格式化返回值 */
  formatResponse: (data: any) => any
  formatAxiosOption: (config: AxiosRequestConfig, option: any) => AxiosRequestConfig
}

export const defaultApiRequestConfig: BaseOption = {
  returnResult: true,
  messageError: true,
  autoRedirect: true,
  throwBizError: false,
  requestOrigin: '',
  method: 'get',
  isJson: false,
  region: 'cn-north-1',
  query: {},
  axiosOptions: {},
  formatResponse: (data) => data,
  formatAxiosOption: (data) => data,
}

export interface ApiRequestMap<T> {
  [key: string]: Partial<T>
}

export interface Res<T> {
  ResponseMetadata: {
    RequestId: string
    Service: string
    Error?: {
      Message: string
    }
  }
  Result?: T
}

export type ApiType<T> = <Params = any, Result = any>(
  params: Params,
  requestConfig?: Partial<T>,
) => Promise<Result>

export abstract class ApiRequest<ExpandOption extends BaseOption> {
  option: ExpandOption

  constructor(option: ExpandOption) {
    this.option = option
  }

  abstract getAxiosUrl(): string

  formatError(e: any) {
    if (e.response) {
      e = e.response

      if (isServer) {
        return e.data
      }

      if (e.status === 401 && this.option.autoRedirect) {
        const loginPath = '/auth/login/'
        // 优先使用返回数据里定义的重定向路径
        const redirectUrl = get(
          e,
          'data.Result.RedirectPath',
          `${loginPath}?redirectURI=${encodeURIComponent(window.location.href)}`,
        )
        location.assign(redirectUrl)
        return
      }
    }
    if (e.data) {
      e = e.data
    }

    return e
  }

  getAxiosOptions<Params>(params: Params) {
    const { method, isJson, axiosOptions, query } = this.option

    if (method.toLowerCase() === 'get') {
      this.option.query = {
        ...query,
        ...params,
      }
    } else {
      if (isJson) {
        axiosOptions.data = params
        axiosOptions.headers = {
          ...axiosOptions.headers,
          ['content-type']: 'application/json; charset=utf-8',
        }
      } else {
        axiosOptions.data = params
        // axiosOptions.headers = {
        //   ...axiosOptions.headers,
        //   ['content-type']: 'application/json; charset=utf-8',
        // }
        // axiosOptions.data = new URLSearchParams(params as any)
      }
    }

    axiosOptions.url = this.getAxiosUrl()

    axiosOptions.headers = {
      ...axiosOptions.headers,
      'x-csrf-token': Cookie.get('csrfToken') || '',
    }
    // 设置请求域名
    if (this.option.requestOrigin) {
      axiosOptions.url = `${this.option.requestOrigin}${axiosOptions.url}`
    }

    axiosOptions.method = this.option.method

    return this.option.formatAxiosOption(axiosOptions, this.option)
  }

  renderWorkflow(url: string) {
    if (!url) return null
    return <Link onClick={() => window.open(url)}>点击申请</Link>
  }

  getAndShowError(e: any) {
    let err = e
    let msg = e.message || '网络繁忙，请稍后重试'
    const body = this.formatError(e)
    if (body && body.ResponseMetadata && body.ResponseMetadata.Error) {
      msg = get(body, 'ResponseMetadata.Error.Message')
      err = new Error(msg)
    }

    if (body.message) {
      msg = body.message
      err = new Error(msg)
    }

    if (this.option.messageError && !isServer) {
      Message.error({
        content: msg,
      })
    }
    return err
  }

  instance: <Params = any, Res = any>(
    params: Params,
    requestConfig?: Partial<ExpandOption>,
  ) => Promise<Res> = async (params, requestConfig) => {
    this.option = _.merge(this.option, requestConfig)
    const finalAxiosOptions = this.getAxiosOptions<typeof params>(params)

    try {
      let resultData: any
      let result: any

      if (isServer) {
        const service = (global as any).__SSR_CTX_SERVICE__

        const { headers = {}, url, method, data: body } = finalAxiosOptions
        const [link, queryString] = url?.split('?') || []
        const query = qs.parse(queryString) || {}
        // 直接请求
        if (/api\/service/.test(link)) {
          const [, , , serviceName, Action] = link.split('/')

          resultData = await service[serviceName][Action]({
            ...query,
            ...body,
          })
        } else {
          const [, , serviceName, psm, path] = link.split('/') || []
          resultData = await service[serviceName].fetchApi(
            {
              body,
              query,
              headers,
              method,
            },
            {
              psm,
              path,
            },
          )
        }
      } else {
        result = await axios(finalAxiosOptions)
        resultData = result.data
      }
      const data = this.option.formatResponse(resultData)

      if (data?.ResponseMetadata?.Error) {
        apiError({
          action: data?.ResponseMetadata?.Action,
          service: data?.ResponseMetadata?.Service,
          version: data?.ResponseMetadata?.Version,
          error: {
            type: data?.ResponseMetadata?.Error?.Code,
            message: data?.ResponseMetadata?.Error?.Message,
            stack: result,
          },
        })
        if (this.option.throwBizError) throw { response: { data: data } }
        else this.getAndShowError(data)
      }
      if (this.option.returnResult) {
        //返回Result为null时，仍然返回ret.Result而不是ret
        // if (data.Result !== undefined) {
        return data.Result
        // }
      }
      return data
    } catch (e) {
      const err = this.getAndShowError(e)
      // 尝试捕获业务错误body
      const body = get(e, 'response.data')
      if (this.option.throwBizError && body) throw body
      throw err
    }
  }
}

export abstract class ApiRequestService<ExpandOption extends BaseOption> {
  option: ExpandOption
  apiMap: ApiRequestMap<ExpandOption>
  serviceInstance: {
    [action: string]: ApiType<ExpandOption>
  }

  constructor(
    apiMap: ApiRequestMap<ExpandOption>,
    option: Partial<ExpandOption>,
    defaultExpandOption: ExpandOption,
  ) {
    this.option = {
      ...defaultApiRequestConfig,
      ...defaultExpandOption,
      ...option,
    }
    this.apiMap = apiMap
    this.serviceInstance = this.getServiceInstance()
  }

  abstract getApiInstance(key: string, option: ExpandOption): ApiType<ExpandOption>

  getServiceInstance(): {
    [action: string]: ApiType<ExpandOption>
  } {
    const serviceInstance: { [action: string]: ApiType<ExpandOption> } = {}

    Object.keys(this.apiMap).map((key) => {
      serviceInstance[key] = this.getApiInstance(key, {
        ...this.option,
        ...this.apiMap[key],
      })
    })

    return serviceInstance
  }

  setTypes<Params = any, Result = any>(
    apiType: ApiType<ExpandOption>,
  ): (params?: Params, requestConfig?: Partial<ExpandOption>) => Promise<Result> {
    return (params, requestConfig) => {
      return apiType(params, requestConfig)
    }
  }
}
